import { useState } from "react";
import { Mail, Loader2, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InquiryForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        arrival: "",
        departure: "",
        guests: "",
        purpose: "",
        message: "",
        consent: false,
    });
    const [state, setState] = useState({
        loading: false,
        success: false,
        error: null,
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setState({ loading: true, success: false, error: null });
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                arrival: form.arrival || null,
                departure: form.departure || null,
                guests: form.guests ? Number(form.guests) : null,
                purpose: form.purpose || null,
                message: form.message.trim(),
                consent: form.consent,
            };
            const res = await fetch(`${API}/inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(
                    data.detail ||
                        "Anfrage konnte nicht gesendet werden. Bitte per E-Mail an info@emsglueck.com.",
                );
            }
            setState({ loading: false, success: true, error: null });
            setForm({
                name: "",
                email: "",
                phone: "",
                arrival: "",
                departure: "",
                guests: "",
                purpose: "",
                message: "",
                consent: false,
            });
        } catch (err) {
            setState({
                loading: false,
                success: false,
                error: err.message || "Unbekannter Fehler.",
            });
        }
    };

    const inputCls =
        "w-full rounded-2xl bg-background/80 border border-border/70 px-4 py-3 text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all";
    const labelCls =
        "font-accent text-[11px] uppercase tracking-[0.2em] text-foreground/70";

    if (state.success) {
        return (
            <div
                data-testid="inquiry-success"
                className="rounded-3xl bg-card border border-border/60 p-10 text-center"
            >
                <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                    <Check size={28} strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-display text-3xl text-foreground">
                    Vielen Dank!
                </h3>
                <p className="mt-3 text-foreground/70 leading-relaxed max-w-md mx-auto">
                    Deine Anfrage ist bei uns eingegangen. Wir melden uns
                    schnellstmöglich mit einem persönlichen Angebot und der
                    aktuellen Verfügbarkeit.
                </p>
                <button
                    type="button"
                    onClick={() =>
                        setState({ loading: false, success: false, error: null })
                    }
                    className="mt-6 inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium border-b border-primary/40"
                >
                    Neue Anfrage stellen
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            data-testid="inquiry-form"
            className="rounded-3xl bg-card border border-border/60 p-6 sm:p-8 md:p-10"
            noValidate
        >
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label className={labelCls} htmlFor="if-name">
                        Name *
                    </label>
                    <input
                        id="if-name"
                        data-testid="inquiry-name"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Vor- und Nachname"
                        className={`${inputCls} mt-2`}
                    />
                </div>
                <div>
                    <label className={labelCls} htmlFor="if-email">
                        E-Mail *
                    </label>
                    <input
                        id="if-email"
                        data-testid="inquiry-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="deine@email.de"
                        className={`${inputCls} mt-2`}
                    />
                </div>
                <div>
                    <label className={labelCls} htmlFor="if-phone">
                        Telefon
                    </label>
                    <input
                        id="if-phone"
                        data-testid="inquiry-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="optional"
                        className={`${inputCls} mt-2`}
                    />
                </div>
                <div>
                    <label className={labelCls} htmlFor="if-purpose">
                        Anlass
                    </label>
                    <select
                        id="if-purpose"
                        data-testid="inquiry-purpose"
                        value={form.purpose}
                        onChange={(e) => update("purpose", e.target.value)}
                        className={`${inputCls} mt-2 bg-background/80`}
                    >
                        <option value="">Bitte wählen …</option>
                        <option value="Beruflich / Projektleitung">Beruflich / Projektleitung</option>
                        <option value="Meyer Werft Einsatz">Meyer Werft Einsatz</option>
                        <option value="Familienurlaub">Familienurlaub</option>
                        <option value="Kurzurlaub im Emsland">Kurzurlaub im Emsland</option>
                        <option value="Langzeitaufenthalt">Langzeitaufenthalt</option>
                        <option value="Sonstiges">Sonstiges</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="if-arrival">
                        Anreise
                    </label>
                    <input
                        id="if-arrival"
                        data-testid="inquiry-arrival"
                        type="date"
                        value={form.arrival}
                        onChange={(e) => update("arrival", e.target.value)}
                        className={`${inputCls} mt-2`}
                    />
                </div>
                <div>
                    <label className={labelCls} htmlFor="if-departure">
                        Abreise
                    </label>
                    <input
                        id="if-departure"
                        data-testid="inquiry-departure"
                        type="date"
                        value={form.departure}
                        onChange={(e) => update("departure", e.target.value)}
                        className={`${inputCls} mt-2`}
                    />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="if-guests">
                        Anzahl Personen
                    </label>
                    <input
                        id="if-guests"
                        data-testid="inquiry-guests"
                        type="number"
                        min={1}
                        max={6}
                        value={form.guests}
                        onChange={(e) => update("guests", e.target.value)}
                        placeholder="z. B. 2"
                        className={`${inputCls} mt-2 max-w-[160px]`}
                    />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="if-message">
                        Nachricht *
                    </label>
                    <textarea
                        id="if-message"
                        data-testid="inquiry-message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        placeholder="Erzähl uns kurz, wann Du kommen möchtest, wie viele Personen und ob es Besonderheiten gibt."
                        className={`${inputCls} mt-2 resize-none`}
                    />
                </div>
            </div>

            <label
                className="mt-5 flex items-start gap-3 text-sm text-foreground/70 cursor-pointer"
                htmlFor="if-consent"
            >
                <input
                    id="if-consent"
                    data-testid="inquiry-consent"
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-border accent-primary"
                />
                <span>
                    Ich bin damit einverstanden, dass meine Angaben zur
                    Bearbeitung der Anfrage gespeichert werden. Die Daten werden
                    nicht an Dritte weitergegeben.
                </span>
            </label>

            {state.error && (
                <div
                    data-testid="inquiry-error"
                    className="mt-4 flex items-start gap-2 rounded-2xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
                >
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{state.error}</span>
                </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                    type="submit"
                    data-testid="inquiry-submit"
                    disabled={state.loading}
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-base font-medium hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {state.loading ? (
                        <>
                            <Loader2 size={18} className="animate-spin" />
                            Wird gesendet …
                        </>
                    ) : (
                        <>
                            <Mail size={18} />
                            Anfrage senden
                        </>
                    )}
                </button>
                <p className="text-xs text-foreground/60 font-accent tracking-wider">
                    Antwort in der Regel innerhalb von 24 Stunden.
                </p>
            </div>
        </form>
    );
}
